<script setup lang="ts">
import useMedia from '@/composables/media'
import dayjs from 'dayjs'

const { t } = useI18n()

const isRecording = ref(false)

const { supportedMimeType, createAudioBlob } = useMedia()

const recordAudio = () =>
  new Promise((resolve) => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const mediaRecorder = new MediaRecorder(stream, { mimeType: supportedMimeType() })
      let audioChunks = []

      mediaRecorder.addEventListener('dataavailable', (event) => {
        audioChunks.push(event.data)
      })

      const start = () => {
        audioChunks = []
        mediaRecorder.start()
      }

      const stop = () =>
        new Promise((resolve) => {
          mediaRecorder.addEventListener('stop', () => {
            const audioBlob = createAudioBlob(audioChunks)
            const audioUrl = URL.createObjectURL(audioBlob)

            emit('onStopRecord', audioUrl, audioBlob)

            resolve({ audioChunks, audioBlob, audioUrl })
          })

          mediaRecorder.stop()
          stream.getAudioTracks().forEach((track) => track.stop())
        })

      resolve({ start, stop })
    })
  })

const recorder = ref()
const audio = ref()
const timerInSeconds = ref<number>(0)
const interval = ref()

const record = async () => {
  recorder.value = await recordAudio()
  emit('onStartRecord')
  recorder.value.start()
  isRecording.value = true
  createTimer()
}

const stop = async () => {
  audio.value = await recorder.value.stop()
  isRecording.value = false
  clearTimer()
}

const createTimer = () => {
  timerInSeconds.value = 0
  interval.value = setInterval(() => {
    timerInSeconds.value++
  }, 1000)
}

const clearTimer = () => {
  if (!isRecording.value) {
    clearInterval(interval.value)
  }
}

watch(timerInSeconds, () => {
  if (timerInSeconds.value >= 120) {
    stop()
  }
})

const formattedTimer = computed(() => {
  return dayjs.duration(timerInSeconds.value, 'seconds').format('mm[:]ss')
})

const emit = defineEmits<{
  (e: 'onStartRecord'): void
  (e: 'onStopRecord', url: string, audioBlob: Blob): void
}>()
</script>

<template>
  <div v-if="isRecording" class="-translate-y-6 rounded-md border p-10 text-center">
    <p class="font-medium text-foreground">
      {{ t('pages.missedCalls.missedCallsDetailPage.audioRecorder.timer.title') }}
    </p>
    <p class="my-10 text-4xl font-bold text-foreground">{{ formattedTimer }}</p>
    <Button variant="outline" @click="stop">
      <BaseIcon name="HeroStopIconSolid" class="h-5 w-5" />{{
        t('pages.missedCalls.missedCallsDetailPage.audioRecorder.timer.stop')
      }}
    </Button>
    <p class="mt-10 text-muted-foreground">
      {{ t('pages.missedCalls.missedCallsDetailPage.audioRecorder.timer.tip') }}
    </p>
  </div>
  <div
    :class="!isRecording ? 'opacity-100 h-auto translate-y-0 transition duration-300' : 'opacity-0 h-0 translate-y-6'"
  >
    <div class="flex h-full flex-col justify-center" :class="isRecording && ' border rounded-md border-input'">
      <div class="h-full px-10 py-12 text-center">
        <p class="mx-auto mb-8 w-36 font-medium text-foreground">
          {{ t('ui.soundRecorder.title') }}
        </p>
        <Button variant="outline" @click="record">
          <BaseIcon name="HeroMicrophoneIconSolid" class="h-3 w-3" />
          {{ t('ui.soundRecorder.button') }}
        </Button>
      </div>
    </div>
  </div>
</template>
